import React, { useEffect, useRef, useState } from "react";
import "./App.css";

const services = [
  {
    title: "Desarrollo Web",
    description:
      "Diseñamos y desarrollamos sitios y plataformas web modernas, rápidas y escalables. Desde una landing de alto impacto hasta productos complejos, creamos experiencias responsive que convierten visitantes en clientes.",
  },
  {
    title: "Desarrollo Mobile",
    description:
      "Construimos apps móviles para iOS y Android con foco en rendimiento, usabilidad y crecimiento. Llevamos tu producto a la mano de tus clientes con una experiencia fluida y confiable.",
  },
  {
    title: "Software a Medida",
    description:
      "Desarrollamos sistemas alineados con tus procesos, objetivos y forma de trabajar. Nada genérico: soluciones personalizadas que mejoran eficiencia, trazabilidad y toma de decisiones.",
  },
  {
    title: "Automatización",
    description:
      "Automatizamos tareas repetitivas e integramos herramientas para reducir errores y tiempos operativos. Tu equipo gana foco para trabajar en lo que realmente mueve el negocio.",
  },
];

const reasons = [
  {
    title: "Experiencia Comprobada",
    description:
      "Contamos con un equipo de desarrolladores altamente capacitados y con amplia experiencia en diversas industrias, garantizando soluciones robustas y eficientes.",
  },
  {
    title: "Soluciones a tu Medida",
    description:
      "Entendemos que cada negocio es único. Por eso, nos enfocamos en crear software que se adapte perfectamente a tus necesidades y objetivos específicos.",
  },
  {
    title: "Calidad que Impulsa Resultados",
    description:
      "Nos comprometemos con los más altos estándares de calidad en cada etapa del desarrollo, asegurando un software fiable, seguro y de alto rendimiento que te ayuda a alcanzar tus metas.",
  },
  {
    title: "Comunicación y Soporte Continuo",
    description:
      "Trabajamos con comunicación transparente de punta a punta y acompañamiento post-lanzamiento para asegurar estabilidad, mejora continua y evolución del producto.",
  },
];

const movingTags = [
  "Web Apps",
  "Mobile",
  "Automatización",
  "Integraciones",
  "Sistemas a Medida",
  "UX/UI",
  "APIs",
  "Escalabilidad",
];

const journeyPhases = [
  {
    phase: "Explorar",
    detail: "Problema, usuarios y oportunidad de negocio.",
  },
  {
    phase: "Diseñar",
    detail: "UX/UI enfocado en claridad, conversión y usabilidad.",
  },
  {
    phase: "Construir",
    detail: "Implementación robusta, escalable y mantenible.",
  },
  {
    phase: "Lanzar",
    detail: "Release controlado con soporte y evolución continua.",
  },
];

const CONTACT_EMAIL = "info@peanutsoftware.com";
const CONTACT_ENDPOINT = process.env.REACT_APP_CONTACT_ENDPOINT || "";
const CONTACT_FORM_TOKEN = process.env.REACT_APP_CONTACT_FORM_TOKEN || "";

function Reveal({
  children,
  direction = "up",
  delay = 0,
  className = "",
  as = "div",
}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const Component = as;
  const classes = [
    "reveal",
    `reveal-${direction}`,
    isVisible ? "is-visible" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Component
      ref={ref}
      className={classes}
      style={{ "--reveal-delay": `${delay}ms` }}
    >
      {children}
    </Component>
  );
}

function App() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    website: "",
  });
  const [formFeedback, setFormFeedback] = useState("");
  const [formFeedbackType, setFormFeedbackType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    if (formFeedback) {
      setFormFeedback("");
      setFormFeedbackType("");
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();

    if (!CONTACT_ENDPOINT) {
      setFormFeedback(
        "Falta configurar el endpoint del formulario. Defini REACT_APP_CONTACT_ENDPOINT.",
      );
      setFormFeedbackType("error");
      return;
    }

    const name = formData.name.trim();
    const email = formData.email.trim();
    const company = formData.company.trim();
    const message = formData.message.trim();
    const website = formData.website.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (website) {
      setFormFeedback("Solicitud recibida.");
      setFormFeedbackType("success");
      return;
    }

    if (!name || !email || !message) {
      setFormFeedback("Completá nombre, email y mensaje para continuar.");
      setFormFeedbackType("error");
      return;
    }

    if (!emailPattern.test(email)) {
      setFormFeedback("Ingresá un email valido para poder responderte.");
      setFormFeedbackType("error");
      return;
    }

    const payload = {
      name,
      email,
      company,
      message,
      website,
      token: CONTACT_FORM_TOKEN,
      source: "peanut-site",
      submittedAt: new Date().toISOString(),
    };

    try {
      setIsSubmitting(true);
      await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "text/plain;charset=utf-8",
        },
        body: JSON.stringify(payload),
      });

      setFormFeedback(
        "Gracias. Recibimos tu consulta y te responderemos pronto.",
      );
      setFormFeedbackType("success");
      setFormData({
        name: "",
        email: "",
        company: "",
        message: "",
        website: "",
      });
    } catch (error) {
      setFormFeedback(
        "No pudimos enviar el formulario en este momento. Probá de nuevo en unos minutos.",
      );
      setFormFeedbackType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="app-shell">
      <section className="hero-section">
        <div className="hero-noise" aria-hidden="true" />
        <div className="hero-stripe hero-stripe-one" aria-hidden="true" />
        <div className="hero-stripe hero-stripe-two" aria-hidden="true" />

        <div className="hero-inner">
          <Reveal className="hero-copy" direction="left">
            <div className="brand-row">
              <img
                src="/images/logo-peanut.png"
                alt="Peanut Software Logo"
                className="hero-logo"
              />
              <div>
                <p className="brand-kicker">Digital Product Studio</p>
                <h1 className="brand-name">
                  <b>peanut</b> software
                </h1>
              </div>
            </div>

            <h2 className="hero-title">
              Soluciones de Software a Medida para Desafíos Complejos
            </h2>
            <p className="hero-description">
              Creamos aplicaciones web y móviles, sistemas a medida y
              automatizaciones eficientes.
            </p>

            <div className="hero-actions">
              <button
                className="cta-button"
                onClick={() => scrollToSection("contact-section")}
              >
                Hablemos de tu Proyecto
              </button>
              <button
                className="ghost-button"
                onClick={() => scrollToSection("services-section")}
              >
                Ver servicios
              </button>
            </div>

            <div className="hero-inline-tags" aria-hidden="true">
              {movingTags.slice(0, 5).map((tag) => (
                <span className="capability-chip" key={`hero-inline-${tag}`}>
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal className="hero-showcase" direction="right" delay={120}>
            <div className="showcase-head">
              <p className="showcase-kicker">Product Journey</p>
              <p className="showcase-badge">Sprint activo</p>
            </div>

            <h3 className="showcase-title">
              De idea a producción en ciclos rápidos y medibles.
            </h3>

            <div className="showcase-track" aria-hidden="true">
              <div className="track-line">
                <span className="track-progress" />
                <span className="track-ping" />
              </div>
              <div className="track-labels">
                <span>Idea</span>
                <span>UX</span>
                <span>Build</span>
                <span>Launch</span>
              </div>
            </div>

            <div className="journey-list">
              {journeyPhases.map((item, index) => (
                <article className="journey-item" key={item.phase}>
                  <span className="journey-index">{`0${index + 1}`}</span>
                  <div>
                    <h4>{item.phase}</h4>
                    <p>{item.detail}</p>
                  </div>
                </article>
              ))}
            </div>

            <div className="showcase-footer" aria-hidden="true">
              <span className="footer-chip">Entrega incremental</span>
              <span className="footer-chip">Feedback continuo</span>
              <span className="footer-chip">Calidad de release</span>
            </div>
          </Reveal>
        </div>
      </section>

      <main className="main-content">
        <section
          id="services-section"
          className="services-section section-shell"
        >
          <Reveal className="section-heading" direction="left">
            <h2>Nuestros Servicios</h2>
            <p className="services-intro">
              En <b>peanut</b>, nos especializamos en transformar ideas en
              soluciones de software robustas y escalables.
            </p>
          </Reveal>

          <div className="services-grid">
            {services.map((service, index) => (
              <Reveal
                as="article"
                className="service-card"
                key={service.title}
                direction={index % 2 === 0 ? "left" : "right"}
                delay={index * 70}
              >
                <h3 className="card-tag">{service.title}</h3>
                <p>{service.description}</p>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="why-us-section section-shell">
          <Reveal className="section-heading" direction="right">
            <h2>
              Por qué <b>peanut</b> software es tu mejor opción
            </h2>
          </Reveal>

          <ul className="why-us-list">
            {reasons.map((reason, index) => (
              <Reveal
                as="li"
                className="why-item"
                key={reason.title}
                direction={index % 2 === 0 ? "right" : "left"}
                delay={index * 60}
              >
                <h4 className="card-tag">{reason.title}</h4>
                <p>{reason.description}</p>
              </Reveal>
            ))}
          </ul>
        </section>

        <section id="contact-section" className="contact-section section-shell">
          <Reveal className="contact-card" direction="up">
            <h2>Contacto</h2>
            <p className="contact-intro">
              Estamos listos para ayudarte a hacer realidad tu proyecto.
            </p>
            <form
              className="contact-form"
              onSubmit={handleContactSubmit}
              noValidate
            >
              <div className="form-grid">
                <label className="form-field" htmlFor="name">
                  <span>Nombre *</span>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label className="form-field" htmlFor="email">
                  <span>Email *</span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="nombre@empresa.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label
                  className="form-field form-ideal-field"
                  htmlFor="website"
                >
                  <span>Sitio web</span>
                  <input
                    id="website"
                    name="website"
                    type="text"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="form-field form-field-full" htmlFor="company">
                  <span>Empresa</span>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    placeholder="Nombre de la empresa (opcional)"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="form-field form-field-full" htmlFor="message">
                  <span>Mensaje *</span>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    placeholder="Contanos brevemente que queres construir."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </label>
              </div>

              <div className="contact-actions">
                <button
                  type="submit"
                  className="cta-button contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Enviando..." : "Enviar consulta"}
                </button>
              </div>
            </form>

            {formFeedback && (
              <p
                className={`form-feedback ${
                  formFeedbackType === "error" ? "is-error" : "is-success"
                }`}
              >
                {formFeedback}
              </p>
            )}

            <p className="contact-mail">Email: {CONTACT_EMAIL}</p>
          </Reveal>
        </section>
      </main>

      <footer className="footer">
        <p>
          © {new Date().getFullYear()} Peanut Software. Todos los derechos
          reservados.
        </p>
      </footer>
    </div>
  );
}

export default App;
